/* eslint-disable complexity */
import React, { Children, useContext } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import Logger from '@lmig/dotcom-utilities-helpers/logger';
import useContactPhone from '@lmig/dotcom-aspect-hooks/useContactPhone';
import { Link } from '@lmig/lmds-react';
import { ContextApp } from '../ContextApp';

const logger = new Logger('client:components:PhoneNumber');

/**
 * Component for rendering the correct phone number by enforcing
 * the myriad of business phone number replacement rules.  See
 * playbook for details on global (page level) phone number replacement.
 *
 * @example
 * Get phone number following the substitution
 * rules for contact numbers based on what is in the
 * application context for contact numbers.  If a
 * number is not found in the app context, the below default
 * is shown.
 * Note: supplying componentName allows for component scoped
 * content managed phone number replacement
 *  <PhoneNumber type="contact" componentName="Footer">
 *    <Link aria-label="Contact us">800-295-2820</Link>
 *  </PhoneNumber>
 *
 * Get phone number following the substitution
 * rules for isvt numbers based on what is in the
 * application context.  If a number is not found
 * in the app context, the below default is shown.
 * @example
 *  <PhoneNumber type="crc" componentName="GetSupport">
 *    <Link className="phoneNumber" aria-label="Contact customer service">
 *      800-290-8711
 *    </Link>
 *  </PhoneNumber>
 *
 */
const Phone = ({
  type = '',
  tel = undefined,
  analyticsAction = 'action telephone',
  componentName = '',
  children = undefined,
}) => {
  const { getContactPhone } = useContactPhone();

  const { phones, replacementPhoneNumbers } = useContext(ContextApp);
  const { asPath = 'unknown' } = useRouter() || '';

  const childText = typeof children === 'string' ? children : null;
  const childNode = !!children && !childText ? children : null;
  const childNodeText = childNode ? children.props.children : null;
  const text = childText || childNodeText || null;

  if (!tel && !text) {
    logger.error(
      `PhoneNumber must be provided a tel number via tel prop or text content as a child on page: ${asPath}`,
    );

    return null;
  }

  const child = childNode ? Children.only(childNode) : <Link />;

  const providedNumber = tel || text;

  const processedFromContext = num => {
    switch (type) {
      case 'contact':
        return getContactPhone(num);
      case 'crc':
        return phones.crc ? phones.crc.number : num;
      case 'claims':
        return phones.claims ? phones.claims.number : num;
      case 'clsc':
        return phones.clsc ? phones.clsc.number : num;
      default:
        return num;
    }
  };

  const processedFromReplacements = num => {
    if (!replacementPhoneNumbers) {
      return num;
    }

    return replacementPhoneNumbers.reduce((acc, currentValue) => {
      const { original, replacement, scope = null } = currentValue;

      if (scope === componentName || scope === null) {
        if (original === num) {
          return replacement;
        }
      }

      return acc;
    }, num);
  };

  // Removes the leading 1- from phone numbers.
  // input: 1-800-555-1234
  // output: 800-555-1234
  const shrinkNumber = num => {
    if (num.startsWith('1-') && num.length > 12) {
      return num.substr(2, 12);
    }

    return num;
  };

  const changedNumber = shrinkNumber(processedFromReplacements(processedFromContext(providedNumber)));

  const telephoneNumber = !!tel && !!text ? text : changedNumber;

  const href = `tel:${changedNumber}`;

  const phoneProps = {
    'data-action': analyticsAction || undefined,
    ...child.props,
    children: telephoneNumber,
    href,
  };

  return React.cloneElement(child, phoneProps);
};

Phone.propTypes = {
  type: PropTypes.oneOf(['', 'contact', 'crc', 'claims', 'clsc']),
  tel: PropTypes.string,
  componentName: PropTypes.string,
  analyticsAction: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.element,
    (props, propName) => {
      const value = props[propName];

      if (typeof value === 'string') {
        // eslint-disable-next-line no-console
        console.error(
          "Warning: You're using a string directly inside <PhoneNumber>. " +
          'Please add an <Link> tag as child of <PhoneNumber>',
        );
      }

      return null;
    },
  ]),
};

export default Phone;
